import React, { useState } from 'react';

function PrivateLeagueCodeModal({ isOpen, onClose, onSubmit }) {
    const [leagueCode, setLeagueCode] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState('');

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!leagueCode.trim()) {
            setError('Please enter a league code');
            return;
        }

        setIsSubmitting(true);
        setError('');

        try {
            const leagueName = leagueCode.trim();
            console.log('PrivateLeagueCodeModal: Attempting to join league:', leagueName);
            await onSubmit(leagueName);
            setLeagueCode('');
            console.log('PrivateLeagueCodeModal: Successfully submitted league name');
            // DON'T call onClose() - AppRouter manages modal transitions!
            // onClose() called by AppRouter after setting up TeamNameModal
        } catch (err) {
            console.error('PrivateLeagueCodeModal: Join failed with error:', err);
            
            // Provide more helpful error messages
            let errorMessage = 'Failed to join league. Please try again.';
            
            if (err.message) {
                if (err.message.includes('not found') || err.message.includes('does not exist')) {
                    errorMessage = 'League not found. Please check the league name and try again.';
                } else if (err.message.includes('full') || err.message.includes('capacity')) {
                    errorMessage = 'League is full. Cannot join at this time.';
                } else if (err.message.includes('already') || err.message.includes('member')) {
                    errorMessage = 'You are already a member of this league.';
                } else if (err.message.includes('permission') || err.message.includes('access')) {
                    errorMessage = 'Access denied. This league may be private or restricted.';
                } else {
                    errorMessage = err.message;
                }
            }
            
            setError(errorMessage);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleClose = () => {
        setLeagueCode('');
        setError('');
        onClose();
    };

    if (!isOpen) return null;

    return (
        <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1000,
            padding: '20px'
        }}>
            <div style={{
                backgroundColor: '#2d3748',
                borderRadius: '16px',
                padding: '30px',
                maxWidth: '450px',
                width: '100%',
                color: '#f7fafc',
                position: 'relative'
            }}>
                {/* Close Button */}
                <button
                    onClick={handleClose}
                    style={{
                        position: 'absolute',
                        top: '15px',
                        right: '15px',
                        backgroundColor: 'transparent',
                        border: 'none',
                        color: '#a0aec0',
                        fontSize: '24px',
                        cursor: 'pointer',
                        padding: '5px',
                        borderRadius: '4px',
                        transition: 'color 0.2s ease'
                    }}
                    onMouseEnter={(e) => e.target.style.color = '#f7fafc'}
                    onMouseLeave={(e) => e.target.style.color = '#a0aec0'}
                >
                    ✕
                </button>

                {/* Header */}
                <div style={{
                    textAlign: 'center',
                    marginBottom: '25px'
                }}>
                    <div style={{
                        fontSize: '48px',
                        marginBottom: '10px'
                    }}>
                        🔒
                    </div>
                    <h2 style={{
                        margin: '0 0 10px 0',
                        fontSize: '24px',
                        color: '#63b3ed'
                    }}>
                        Join Private League
                    </h2>
                    <div style={{
                        margin: 0,
                        color: '#a0aec0',
                        fontSize: '14px'
                    }}>
                        <p style={{ margin: '0 0 8px 0' }}>
                            Enter the league code provided by your league creator.
                        </p>
                        <p style={{ margin: 0, fontSize: '12px', fontStyle: 'italic' }}>
                            A team will be created automatically with your username.
                        </p>
                    </div>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit}>
                    <div style={{ marginBottom: '20px' }}>
                        <label style={{
                            display: 'block',
                            marginBottom: '8px',
                            color: '#e2e8f0',
                            fontSize: '14px',
                            fontWeight: '500'
                        }}>
                            League Code
                        </label>
                        <input
                            type="text"
                            value={leagueCode}
                            onChange={(e) => setLeagueCode(e.target.value)}
                            placeholder="A7B9K3M2"
                            style={{
                                width: '100%',
                                padding: '12px',
                                backgroundColor: '#4a5568',
                                color: '#f7fafc',
                                border: '2px solid #4a5568',
                                borderRadius: '8px',
                                fontSize: '16px',
                                transition: 'border-color 0.2s ease'
                            }}
                            onFocus={(e) => e.target.style.borderColor = '#63b3ed'}
                            onBlur={(e) => e.target.style.borderColor = '#4a5568'}
                            maxLength={50}
                            disabled={isSubmitting}
                        />
                        <div style={{
                            fontSize: '12px',
                            color: '#a0aec0',
                            marginTop: '6px'
                        }}>
                            League codes look like: "A7B9K3M2"
                        </div>
                    </div>

                    {error && (
                        <div style={{
                            backgroundColor: 'rgba(229, 62, 62, 0.1)',
                            border: '1px solid #e53e3e',
                            borderRadius: '6px',
                            padding: '12px',
                            marginBottom: '20px',
                            color: '#fc8181',
                            fontSize: '14px',
                            textAlign: 'center'
                        }}>
                            {error}
                        </div>
                    )}

                    {/* Action Buttons */}
                    <div style={{
                        display: 'flex',
                        gap: '12px',
                        justifyContent: 'flex-end'
                    }}>
                        <button
                            type="button"
                            onClick={handleClose}
                            disabled={isSubmitting}
                            style={{
                                padding: '12px 24px',
                                backgroundColor: '#4a5568',
                                color: '#f7fafc',
                                border: 'none',
                                borderRadius: '8px',
                                cursor: isSubmitting ? 'not-allowed' : 'pointer',
                                fontSize: '14px',
                                opacity: isSubmitting ? 0.6 : 1,
                                transition: 'background-color 0.2s ease'
                            }}
                            onMouseEnter={(e) => {
                                if (!isSubmitting) e.target.style.backgroundColor = '#2d3748';
                            }}
                            onMouseLeave={(e) => {
                                if (!isSubmitting) e.target.style.backgroundColor = '#4a5568';
                            }}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={isSubmitting || !leagueCode.trim()}
                            style={{
                                padding: '12px 24px',
                                backgroundColor: (!leagueCode.trim() || isSubmitting) ? '#4a5568' : '#4299e1',
                                color: 'white',
                                border: 'none',
                                borderRadius: '8px',
                                cursor: (!leagueCode.trim() || isSubmitting) ? 'not-allowed' : 'pointer',
                                fontSize: '14px',
                                fontWeight: 'bold',
                                transition: 'background-color 0.2s ease',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                opacity: (!leagueCode.trim() || isSubmitting) ? 0.6 : 1
                            }}
                            onMouseEnter={(e) => {
                                if (leagueCode.trim() && !isSubmitting) {
                                    e.target.style.backgroundColor = '#3182ce';
                                }
                            }}
                            onMouseLeave={(e) => {
                                if (leagueCode.trim() && !isSubmitting) {
                                    e.target.style.backgroundColor = '#4299e1';
                                }
                            }}
                        >
                            {isSubmitting ? (
                                <>
                                    <div style={{
                                        width: '14px',
                                        height: '14px',
                                        border: '2px solid #ffffff',
                                        borderTopColor: 'transparent',
                                        borderRadius: '50%',
                                        animation: 'spin 1s linear infinite'
                                    }}></div>
                                    Joining...
                                </>
                            ) : (
                                'Join League'
                            )}
                        </button>
                    </div>
                </form>

                {/* Help Text */}
                <div style={{
                    marginTop: '20px',
                    paddingTop: '20px',
                    borderTop: '1px solid #4a5568',
                    fontSize: '12px',
                    color: '#a0aec0',
                    textAlign: 'center'
                }}>
                    Don't have a league ID? Ask your league creator to share it with you.
                </div>
            </div>
        </div>
    );
}

export default PrivateLeagueCodeModal;